// Bottom mobile nav + "More" slide-up menu — mirrors vanilla .mobile-nav + .mobile-more-menu.
// Visible only on screens ≤ 700px.

import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'

// Tabs shown directly in the bottom bar (primary)
const PRIMARY_TABS_DEFAULT = ['overview', 'projects', 'estimates', 'expenses']
const PRIMARY_TABS_CREW    = ['overview', 'projects', 'timelog']

const TAB_ICONS: Record<string, string> = {
  overview:  '🏠', projects: '🏗️', estimates: '📐', expenses: '💰',
  timelog:   '🕐', payments: '💵', clients:   '👤', subs:     '👷',
  vendors:   '🏪', bidroom:  '📋', portfolio: '💰', reports:  '📊',
  options:   '⚙️',
}
const TAB_LABELS: Record<string, string> = {
  overview: 'Home', projects: 'Projects', estimates: 'Estimates', expenses: 'Expenses',
  timelog: 'My Hours', payments: 'Payments', clients: 'Clients', subs: 'Subs',
  vendors: 'Vendors', bidroom: 'Bid Room', portfolio: 'Portfolio', reports: 'Reports',
  options: 'Options',
}

function tabPath(key: string) {
  return key === 'overview' ? '/' : `/${key}`
}

export default function MobileNav() {
  const { tabs, role } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)

  const isCrew = role === 'field_crew'
  const primaryKeys = isCrew ? PRIMARY_TABS_CREW : PRIMARY_TABS_DEFAULT
  const primaryTabs = primaryKeys.filter((k) => tabs.includes(k))
  const moreTabs    = tabs.filter((k) => !primaryKeys.includes(k))

  function isActive(key: string) {
    const path = tabPath(key)
    return path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
  }

  function goTo(key: string) {
    navigate(tabPath(key))
    setMoreOpen(false)
  }

  const moreIsActive = moreTabs.some(isActive)

  return (
    <>
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
            onClick={() => setMoreOpen(false)}
            aria-hidden
          />
          <div className="mobile-more-menu" role="menu">
            {moreTabs.map((key) => (
              <button key={key} role="menuitem" onClick={() => goTo(key)}>
                {TAB_ICONS[key] ?? '•'} {TAB_LABELS[key] ?? key}
              </button>
            ))}
          </div>
        </>
      )}

      <nav className="mobile-nav" aria-label="Mobile navigation">
        {primaryTabs.map((key) => (
          <button
            key={key}
            className={isActive(key) ? 'active' : ''}
            onClick={() => goTo(key)}
            aria-current={isActive(key) ? 'page' : undefined}
          >
            <span className="mob-icon">{TAB_ICONS[key] ?? '•'}</span>
            {TAB_LABELS[key] ?? key}
          </button>
        ))}
        {moreTabs.length > 0 && (
          <button
            className={moreIsActive ? 'active' : ''}
            onClick={() => setMoreOpen((o) => !o)}
            aria-expanded={moreOpen}
            aria-haspopup="menu"
          >
            <span className="mob-icon">☰</span>
            More
          </button>
        )}
      </nav>
    </>
  )
}
